import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchNotifications, markNotificationAsRead } from "../../action";
import { decodeId, formatDateFromISO } from "../../utils/helper";

const PopoverStyled = styled(Popover)(({ theme }) => ({
  "& .MuiPaper-root": {
    position: "relative",
    width: "300px", // Set a fixed width or a percentage to control the size
    maxWidth: "90%", // Ensure it doesn’t overflow the screen width
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: -8,
    left: "calc(50% - 8px)",
    width: 0,
    height: 0,
    borderLeft: "8px solid transparent",
    borderRight: "8px solid transparent",
    borderBottom: `8px solid ${theme.palette.background.paper}`,
    zIndex: 1,
  },
}));

const ClearAllButtonStyled = styled(Button)(({ theme }) => ({
  padding: 0,
  minHeight: "auto",
  minWidth: "auto",
  color: theme.palette.primary.main,
}));

const NotificationDialog = ({
  anchorEl,
  onClose,
  open,
  id,
  notificationData,
  userId,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [hasFetchedMore, setHasFetchedMore] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const SignInSuccess = useSelector((state) => state?.profileReducer);

  const userLanguage = localStorage.getItem("selectedLanguage");
  const listRef = useRef(null);

  // useEffect(() => {
  //   const userId = SignInSuccess?.profileReducer?.currentUserResponseData?.id;

  //   if(userId){
  //   dispatch(fetchNotifications((userId)))};
  // }, [open]);

  useEffect(() => {
    if (notificationData) {
      setNotifications(notificationData);
    }
  }, [notificationData]);

  const handleClearAll = () => {
    const res = dispatch(
      markNotificationAsRead({ readAll: true, userId: userId }, "label")
    );
    if (!!res) {
      setNotifications([]);
      dispatch(fetchNotifications(userId));
    }
  };

  const handleListItemClick = (notification) => {
    if (!!notification.redirectUrl) {
      navigate(`/fosa${notification.redirectUrl}`);
      dispatch(markNotificationAsRead({ ...notification, userId: userId }));
      onClose();
    }
  };

  const getNotificationMessage = (message) => {
    try {
      const parsedMessage = JSON.parse(message);
      return parsedMessage[userLanguage] || parsedMessage["en"];
    } catch (error) {
      // console.error("Failed to parse notification message:", error);
      return message; // Fallback to the original message if parsing fails
    }
  };

  const handleScroll = () => {
    const threshold = 1;
    const isBottom =
      listRef.current.scrollHeight - listRef.current.scrollTop <=
      listRef.current.clientHeight + threshold;
    if (isBottom && !hasFetchedMore) {
      dispatch(fetchNotifications(userId, SignInSuccess.notificationListTotalCount));
      setHasFetchedMore(true); // Set to true to prevent further API calls
    }
  };

  return (
    <div>
      <PopoverStyled
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 16,
          }}
        >
          <Typography variant="h6">{t("notificationTitle")}</Typography>
          <ClearAllButtonStyled
            onClick={handleClearAll}
            disabled={notifications.length === 0}
          >
            {t("clearAll")}
          </ClearAllButtonStyled>
        </div>
        <Divider />
        <List
          ref={listRef}
          style={{ width: 300, maxHeight: 200, overflowY: "auto" }}
          onScroll={handleScroll}
        >
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <React.Fragment key={index}>
                <ListItem
                  button
                  onClick={() => handleListItemClick(notification)}
                >
                  <ListItemIcon>
                    <CheckCircleIcon style={{ color: "green" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${getNotificationMessage(notification.message)}`}
                    secondary={formatDateFromISO(notification.createdAt)}
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))
          ) : (
            <Typography style={{ padding: 16 }} align="center">
              {t("emptyNotification")}
            </Typography>
          )}
        </List>
      </PopoverStyled>
    </div>
  );
};

export default NotificationDialog;
